// TanStack Query hooks that wrap AdminClient. Query keys are
// `[resourceId, action, params?]` so cache invalidation is precise.

import * as React from 'react'
import {
  hashKey,
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import { useAdminClient } from './provider.js'
import type {
  AdminConfig,
  AdminFeatures,
  CustomActionResponse,
  CurrentUser,
  ListQuery,
  ListResponse,
  RecordResponse,
  ResourceJSON,
} from './types.js'
import { resolveFeatures } from './types.js'
import {
  AdminApiError,
  type AuthUiProps,
  type AuditLogQuery,
  type AuditLogResponse,
  type CacheStatsResponse,
  type GlobalSearchResponse,
  type HistoryListResponse,
  type HistoryRevisionResponse,
  type TimeSeriesQuery,
  type TimeSeriesResponse,
} from './client.js'
import { useI18n } from './i18n.js'

const KEY_CONFIG = ['modern-admin', 'config'] as const
const keyList = (resourceId: string, query?: ListQuery) =>
  ['modern-admin', resourceId, 'list', query ?? null] as const
const keyShow = (resourceId: string, recordId: string) =>
  ['modern-admin', resourceId, 'show', recordId] as const
const keyHistory = (resourceId: string, recordId: string) =>
  ['modern-admin', resourceId, 'history', recordId] as const
const keyHistoryRevision = (resourceId: string, recordId: string, revisionId: string) =>
  ['modern-admin', resourceId, 'history', recordId, revisionId] as const
const keyAuditLog = (query?: AuditLogQuery) =>
  ['modern-admin', 'audit-log', query ?? null] as const
const keyActionData = (
  resourceId: string,
  actionName: string,
  recordId?: string,
  recordIds?: readonly string[],
) =>
  [
    'modern-admin',
    resourceId,
    'action',
    actionName,
    recordId ?? null,
    recordIds?.length ? recordIds.join(',') : null,
  ] as const

/**
 * Resources whose cached queries embed data of `resourceId` or aggregate
 * over it — computed from the bootstrap config's reference graph:
 *
 *   * resources with a property referencing `resourceId` (their list rows
 *     render its records via `populated`),
 *   * resources that `resourceId` itself references (their show pages
 *     surface child rows / counts through related-records tables).
 *
 * Mirrors the server-side dependents map in `ModernAdmin` so both cache
 * layers drop the same scopes on a mutation.
 */
const linkedResourceIds = (
  config: AdminConfig | undefined,
  resourceId: string,
): Set<string> => {
  const linked = new Set<string>()
  for (const resource of config?.resources ?? []) {
    if (resource.id === resourceId) {
      for (const property of resource.properties) {
        if (property.reference) linked.add(property.reference)
      }
    } else if (resource.properties.some((p) => p.reference === resourceId)) {
      linked.add(resource.id)
    }
  }
  linked.delete(resourceId)
  return linked
}

/**
 * Central client-side invalidation for a mutation on `resourceId`. Marks
 * stale (active queries refetch immediately, inactive on next mount):
 *
 *   * everything under `['modern-admin', <resourceId>]` — list/show/
 *     values/search of the mutated resource,
 *   * the same prefix for every linked resource (see `linkedResourceIds`)
 *     so populated reference labels and related-record tables refresh,
 *   * cross-resource aggregate caches that key a literal at index 1 and
 *     therefore never prefix-match a resource id: field suggestions, the
 *     user directory, global search, dashboard time series, audit log.
 *
 * Exported for realtime wiring and imperative call sites; the mutation
 * hooks below all route through it.
 */
export const invalidateResourceData = (qc: QueryClient, resourceId: string): void => {
  void qc.invalidateQueries({ queryKey: ['modern-admin', resourceId] })
  const config = qc.getQueryData<AdminConfig>(KEY_CONFIG)
  for (const linked of linkedResourceIds(config, resourceId)) {
    void qc.invalidateQueries({ queryKey: ['modern-admin', linked] })
  }
  void qc.invalidateQueries({ queryKey: ['modern-admin', 'fieldSuggestions', resourceId] })
  void qc.invalidateQueries({ queryKey: ['modern-admin', 'user-dir', resourceId] })
  void qc.invalidateQueries({ queryKey: ['modern-admin', 'global-search'] })
  void qc.invalidateQueries({ queryKey: ['modern-admin', 'timeseries'] })
  void qc.invalidateQueries({ queryKey: ['modern-admin', 'audit-log'] })
  void qc.invalidateQueries({ queryKey: ['modern-admin', 'audit-log-infinite'] })
}

export const useAdminConfig = (): UseQueryResult<AdminConfig> => {
  const client = useAdminClient()
  return useQuery({ queryKey: KEY_CONFIG, queryFn: () => client.config(), staleTime: 60_000 })
}

export const useResource = (resourceId: string | undefined): ResourceJSON | undefined => {
  const { data } = useAdminConfig()
  const { localizeResource } = useI18n()
  return React.useMemo(() => {
    const resource = data?.resources.find((r) => r.id === resourceId)
    return resource ? localizeResource(resource) : undefined
  }, [data?.resources, localizeResource, resourceId])
}

/**
 * Capability flags advertised by the backend via `/admin/api/config`.
 * Use to gate optional UI surfaces (audit-log link, settings sections,
 * revisions button, AI assistant widget) — every flag is `false` until
 * the bootstrap config is loaded, so consumers can render unconditionally
 * and the gating logic short-circuits during the initial paint.
 */
export const useFeatures = (): AdminFeatures => {
  const { data } = useAdminConfig()
  return React.useMemo(() => resolveFeatures(data?.features), [data?.features])
}

export const useResources = (): ResourceJSON[] => {
  const { data } = useAdminConfig()
  const { localizeResource } = useI18n()
  return React.useMemo(
    () => (data?.resources ?? []).map((resource) => localizeResource(resource)),
    [data?.resources, localizeResource],
  )
}

/**
 * Fetch distinct values for a field, cached for 5 minutes.
 * Used by the filter value picker to offer multi-select when cardinality is low.
 * The `enabled` flag allows lazy loading only when the filter UI is open.
 */
export const useDistinctValues = (
  resourceId: string,
  field: string,
  options?: { search?: string; limit?: number; enabled?: boolean },
): UseQueryResult<{ values: string[]; hasMore: boolean }> => {
  const client = useAdminClient()
  return useQuery({
    queryKey: ['modern-admin', resourceId, 'values', field, options?.search ?? '', options?.limit ?? 100] as const,
    queryFn: () => client.distinctValues(resourceId, field, {
      search: options?.search,
      limit: options?.limit,
    }),
    staleTime: 5 * 60_000, // 5 min cache
    enabled: options?.enabled !== false,
  })
}

/**
 * Query-key hashes whose next fetch must bypass the server caches. Set by
 * `useRefreshRecords`, consumed (and cleared) by the `useRecords` fetcher.
 *
 * TanStack's `refetch()` carries no per-call arguments, and the flag has
 * to reach the fetcher of an *existing* query — a piece of component
 * state wouldn't, since the refetch may be triggered from anywhere the
 * key is known. Keyed by the structural hash, so a stale marker can at
 * worst cost one extra uncached round-trip.
 */
const pendingListRefresh = new Set<string>()

export const useRecords = (
  resourceId: string,
  query?: ListQuery,
): UseQueryResult<ListResponse> => {
  const client = useAdminClient()
  const key = keyList(resourceId, query)
  return useQuery({
    queryKey: key,
    queryFn: () => {
      const refresh = pendingListRefresh.delete(hashKey(key))
      return client.list(resourceId, query, refresh ? { refresh: true } : undefined)
    },
    // Keep the previous page/sort/filter results on screen while the next
    // query loads (and during background invalidations) instead of blanking
    // the table to skeletons. Skeletons show only on the true first load
    // (`isPending`), where there is no prior data to hold.
    placeholderData: keepPreviousData,
  })
}

/**
 * Refetch a list query past every cache in the chain — browser, HTTP
 * response cache, action cache — so the user gets what the database holds
 * right now. Unchanged data is simply re-served; changed data also drops
 * the resource's server-side cached scopes (see `CacheRuntime.revalidate`).
 *
 * Deliberately narrower than `invalidateResourceData`: refreshing a list
 * is a read, so it must not mark half the client cache stale and set off a
 * cascade of refetches.
 */
export const useRefreshRecords = (
  resourceId: string,
  query?: ListQuery,
): (() => Promise<void>) => {
  const qc = useQueryClient()
  return React.useCallback(async () => {
    const key = keyList(resourceId, query)
    const hash = hashKey(key)
    pendingListRefresh.add(hash)
    try {
      await qc.refetchQueries({ queryKey: key, exact: true })
    } finally {
      // No-op when the fetcher consumed it; clears the marker when the
      // query was removed/disabled before it could run.
      pendingListRefresh.delete(hash)
    }
  }, [qc, resourceId, query])
}

export const useRecord = (
  resourceId: string,
  recordId: string | undefined,
): UseQueryResult<RecordResponse> => {
  const client = useAdminClient()
  return useQuery({
    queryKey: keyShow(resourceId, recordId ?? ''),
    queryFn: () => client.show(resourceId, recordId!),
    enabled: !!recordId,
  })
}

export const useCreateRecord = (
  resourceId: string,
): UseMutationResult<RecordResponse, Error, Record<string, unknown>> => {
  const client = useAdminClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload) => client.create(resourceId, payload),
    onSuccess: () => {
      invalidateResourceData(qc, resourceId)
    },
  })
}

export const useUpdateRecord = (
  resourceId: string,
): UseMutationResult<
  RecordResponse,
  Error,
  { id: string; payload: Record<string, unknown> }
> => {
  const client = useAdminClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }) => client.update(resourceId, id, payload),
    onSuccess: () => {
      invalidateResourceData(qc, resourceId)
    },
  })
}

export const useDeleteRecord = (
  resourceId: string,
): UseMutationResult<void, Error, string> => {
  const client = useAdminClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id) => client.delete(resourceId, id),
    onSuccess: () => {
      invalidateResourceData(qc, resourceId)
    },
  })
}

export const useBulkDeleteRecords = (
  resourceId: string,
): UseMutationResult<unknown, Error, ReadonlyArray<string>> => {
  const client = useAdminClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (ids) => client.bulkDelete(resourceId, ids),
    onSuccess: () => {
      invalidateResourceData(qc, resourceId)
    },
  })
}

const keySearch = (resourceId: string, query: string) =>
  ['modern-admin', resourceId, 'search', query] as const

/**
 * Live-search hook against a resource's `search` action. Used by reference
 * comboboxes — debounce the input on the call site.
 */
// ─── Auth ─────────────────────────────────────────────────────────────────

const KEY_ME = ['modern-admin', 'auth', 'me'] as const
const KEY_AUTH_UI = ['modern-admin', 'auth', 'ui-props'] as const

export interface CurrentUserResult {
  user: CurrentUser | null
  isLoading: boolean
  isAuthenticated: boolean
  error: Error | null
}

/** Resolve the current admin via /admin/api/auth/me. A 401 response surfaces
 *  as `user: null` (rather than an error) so callers can branch on it to
 *  render the login screen. */
export const useCurrentUser = (): CurrentUserResult => {
  const client = useAdminClient()
  const query = useQuery<{ user: CurrentUser } | null, Error>({
    queryKey: KEY_ME,
    queryFn: async () => {
      try {
        return await client.me()
      } catch (err) {
        if (err instanceof AdminApiError && err.status === 401) return null
        throw err
      }
    },
    staleTime: 30_000,
    retry: (failureCount, err) => {
      if (err instanceof AdminApiError && err.status === 401) return false
      return failureCount < 1
    },
  })
  return {
    user: query.data?.user ?? null,
    isLoading: query.isLoading,
    isAuthenticated: !!query.data?.user,
    error: query.error,
  }
}

/** Fetch public auth UI metadata (enabled social providers, email/password flag).
 *  Cached indefinitely — the provider list is static for a given deployment. */
export const useAuthUiProps = (): UseQueryResult<AuthUiProps> => {
  const client = useAdminClient()
  return useQuery({
    queryKey: KEY_AUTH_UI,
    queryFn: () => client.getAuthUiProps(),
    staleTime: Infinity,
  })
}

/** Initiate OAuth social login. Navigates the browser away to the provider;
 *  `isPending` is true while the redirect URL is being fetched. */
export const useSocialLogin = (): UseMutationResult<void, Error, string> => {
  const client = useAdminClient()
  return useMutation({
    mutationFn: (provider: string) => client.loginSocial(provider),
  })
}

export const useLogin = (): UseMutationResult<
  void,
  Error,
  { email: string; password: string }
> => {
  const client = useAdminClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ email, password }) => client.login(email, password),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY_ME })
      qc.invalidateQueries({ queryKey: KEY_CONFIG })
    },
  })
}

export const useLogout = (): UseMutationResult<void, Error, void> => {
  const client = useAdminClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => client.logout(),
    onSuccess: async () => {
      // Cancel any in-flight `me` refetch so it cannot overwrite the
      // optimistic null below and bounce the gate back to authenticated.
      await qc.cancelQueries({ queryKey: KEY_ME })
      // Flip the auth gate to "logged out" immediately. We deliberately
      // do NOT invalidate KEY_ME here — Better Auth has already deleted
      // the server-side session, but the Set-Cookie header may not be
      // applied to outgoing requests for a tick, and a refetch in that
      // window would return the still-valid user and cancel the logout
      // visually. The next mount/refresh will re-check freshly.
      qc.setQueryData(KEY_ME, null)
      // Drop every other cached resource so list/show data doesn't
      // linger behind the login form.
      qc.removeQueries({
        predicate: (q) => {
          const k = q.queryKey as readonly unknown[]
          return !(k[0] === 'modern-admin' && k[1] === 'auth' && k[2] === 'me')
        },
      })
    },
  })
}

export const useInvokeRecordAction = (
  resourceId: string,
): UseMutationResult<
  CustomActionResponse,
  Error,
  { recordId: string; actionName: string; payload?: Record<string, unknown> }
> => {
  const client = useAdminClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ recordId, actionName, payload }) =>
      client.invokeRecordAction(resourceId, recordId, actionName, payload),
    onSuccess: () => {
      invalidateResourceData(qc, resourceId)
    },
  })
}

/**
 * Priming query for a custom action's UI — issues the `GET` variant of the
 * action route so the handler runs with `method: 'get'` and can hand the
 * component whatever it needs to render (templates, counts, defaults).
 *
 * Disabled by default for actions with no `component`: a plain menu entry
 * fires straight to `POST` and must not trigger the handler twice.
 */
export const useActionData = (
  resourceId: string,
  actionName: string,
  options: { recordId?: string; recordIds?: readonly string[]; enabled?: boolean } = {},
): UseQueryResult<CustomActionResponse> => {
  const client = useAdminClient()
  const { recordId, recordIds, enabled = true } = options
  return useQuery({
    queryKey: keyActionData(resourceId, actionName, recordId, recordIds),
    queryFn: () =>
      recordId
        ? client.fetchRecordAction(resourceId, recordId, actionName)
        : client.fetchResourceAction(
          resourceId,
          actionName,
          // Bulk actions prime with the selection so the handler sees the
          // same `context.records` it will get on submit. Bounded by the
          // list page's server-capped `perPage`, so the query string stays
          // well inside header limits.
          recordIds?.length ? { recordIds: recordIds.join(',') } : {},
        ),
    enabled: enabled && Boolean(resourceId) && Boolean(actionName),
    // The handler is user code that may have observable effects; never
    // replay it on window focus or remount without an explicit refetch.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

export const useInvokeBulkAction = (
  resourceId: string,
): UseMutationResult<
  CustomActionResponse,
  Error,
  { actionName: string; ids: string[]; payload?: Record<string, unknown> }
> => {
  const client = useAdminClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ actionName, ids, payload }) =>
      client.invokeBulkAction(resourceId, actionName, ids, payload),
    onSuccess: () => {
      invalidateResourceData(qc, resourceId)
    },
  })
}

export const useInvokeResourceAction = (
  resourceId: string,
): UseMutationResult<CustomActionResponse, Error, { actionName: string; payload?: Record<string, unknown> }> => {
  const client = useAdminClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ actionName, payload }) => client.invokeResourceAction(resourceId, actionName, payload),
    onSuccess: () => {
      invalidateResourceData(qc, resourceId)
    },
  })
}

/**
 * Cross-resource search hook. Fires a single batched request that fans out
 * to every registered resource's `search` action; results are grouped by
 * resource. The empty-query case is handled by the caller (skip render);
 * `enabled` allows lazy activation while the dialog is closed.
 */
export const useGlobalSearch = (
  query: string,
  enabled = true,
): UseQueryResult<GlobalSearchResponse> => {
  const client = useAdminClient()
  return useQuery({
    queryKey: ['modern-admin', 'global-search', query] as const,
    // Forward the AbortSignal TanStack Query attaches to each invocation.
    // The signal fires when the query key changes (next keystroke) or the
    // component unmounts, letting the server short-circuit stale work.
    queryFn: ({ signal }) => client.globalSearch(query, undefined, { signal }),
    enabled: enabled && query.trim().length > 0,
    staleTime: 30_000,
  })
}

export const useSearchRecords = (
  resourceId: string | undefined,
  query: string,
  enabled = true,
): UseQueryResult<ListResponse> => {
  const client = useAdminClient()
  return useQuery({
    queryKey: keySearch(resourceId ?? '', query),
    queryFn: () => client.search(resourceId!, query),
    enabled: !!resourceId && enabled,
    staleTime: 30_000,
  })
}

/**
 * Distinct (deduplicated, sorted) values pulled from a single field of a
 * resource — the data source for the autocomplete `suggestionsResource +
 * suggestionsField` binding on `KeyValueFieldSpec`. Loads up to `perPage`
 * records and projects `field` client-side; for typical admin resources
 * (hundreds–low thousands of rows) this is plenty cheap. For very large
 * tables, reach for a dedicated `distinct` endpoint.
 */
export const useFieldSuggestions = (
  resourceId: string | undefined,
  field: string | undefined,
  perPage = 200,
): UseQueryResult<string[]> => {
  const client = useAdminClient()
  return useQuery({
    queryKey: ['modern-admin', 'fieldSuggestions', resourceId ?? '', field ?? '', perPage],
    queryFn: async (): Promise<string[]> => {
      const res = await client.list(resourceId!, { perPage })
      const seen = new Set<string>()
      const out: string[] = []
      for (const r of res.records) {
        const raw = r.params?.[field!]
        if (raw == null || raw === '') continue
        const v = String(raw)
        if (seen.has(v)) continue
        seen.add(v)
        out.push(v)
      }
      out.sort((a, b) => a.localeCompare(b))
      return out
    },
    enabled: !!resourceId && !!field,
    staleTime: 60_000,
  })
}

export const useTimeSeries = (
  query: TimeSeriesQuery | null,
): UseQueryResult<TimeSeriesResponse> => {
  const client = useAdminClient()
  return useQuery({
    queryKey: ['modern-admin', 'timeseries', query],
    queryFn: () => client.timeseries(query!),
    enabled: query !== null && !!query.resource && !!query.dateField,
    staleTime: 60_000,
  })
}

export const useRecordHistory = (
  resourceId: string,
  recordId: string | undefined,
  options: { limit?: number; offset?: number } = {},
): UseQueryResult<HistoryListResponse> => {
  const client = useAdminClient()
  return useQuery({
    queryKey: [...keyHistory(resourceId, recordId ?? ''), options] as const,
    queryFn: () => client.listHistory(resourceId, recordId!, options),
    enabled: !!resourceId && !!recordId,
    staleTime: 30_000,
  })
}

export const useHistoryRevision = (
  resourceId: string,
  recordId: string | undefined,
  revisionId: string | undefined,
): UseQueryResult<HistoryRevisionResponse> => {
  const client = useAdminClient()
  return useQuery({
    queryKey: keyHistoryRevision(resourceId, recordId ?? '', revisionId ?? ''),
    queryFn: () => client.getHistoryRevision(resourceId, recordId!, revisionId!),
    enabled: !!resourceId && !!recordId && !!revisionId,
    staleTime: 30_000,
  })
}

export const useRevertRevision = (
  resourceId: string,
  recordId: string,
): UseMutationResult<RecordResponse, Error, { revisionId: string; reason?: string }> => {
  const client = useAdminClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ revisionId, reason }) =>
      client.revertHistoryRevision(resourceId, recordId, revisionId, { reason }),
    onSuccess: () => {
      // `['modern-admin', resourceId]` already prefix-covers show/history/
      // revision keys of this record; the helper adds linked resources +
      // aggregate caches.
      invalidateResourceData(qc, resourceId)
    },
  })
}

export const useAuditLog = (
  query: AuditLogQuery = {},
): UseQueryResult<AuditLogResponse> => {
  const client = useAdminClient()
  return useQuery({
    queryKey: keyAuditLog(query),
    queryFn: () => client.listAuditLog(query),
    staleTime: 30_000,
  })
}

/**
 * Cursor-based infinite scroll variant of `useAuditLog`.
 * Each page passes the `at` timestamp of the last entry as the `before` cursor.
 * `pageSize` entries are requested; if the response is full, there are more pages.
 */
export const useInfiniteAuditLog = (
  filters: Omit<AuditLogQuery, 'before' | 'offset' | 'limit'>,
  pageSize: number,
): UseInfiniteQueryResult<InfiniteData<AuditLogResponse>, Error> => {
  const client = useAdminClient()
  return useInfiniteQuery({
    queryKey: ['modern-admin', 'audit-log-infinite', filters],
    queryFn: ({ pageParam }) =>
      client.listAuditLog({
        ...filters,
        limit: pageSize + 1,
        before: pageParam as number | undefined,
      }),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) => {
      const events = lastPage.events
      if (events.length <= pageSize) return undefined
      return events[pageSize - 1]!.at
    },
    staleTime: 30_000,
  })
}

const CACHE_STATS_QUERY_KEY = ['modern-admin', 'cache-stats'] as const

export const clearCacheStatsEntries = (stats: CacheStatsResponse): CacheStatsResponse => ({
  ...stats,
  entries: [],
})

export const useCacheStats = (): UseQueryResult<CacheStatsResponse> => {
  const client = useAdminClient()
  return useQuery({
    queryKey: CACHE_STATS_QUERY_KEY,
    queryFn: () => client.cacheStats(),
    refetchInterval: 10_000,
  })
}

export const useInvalidateResourceCache = (): UseMutationResult<
  { ok: true },
  Error,
  string
> => {
  const client = useAdminClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (resourceId) => client.invalidateResourceCache(resourceId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CACHE_STATS_QUERY_KEY })
    },
  })
}

interface ResetCacheStatsContext {
  previous?: CacheStatsResponse
}

export const useResetCacheStats = (): UseMutationResult<
  CacheStatsResponse,
  Error,
  void,
  ResetCacheStatsContext
> => {
  const client = useAdminClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => client.resetCacheStats(),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: CACHE_STATS_QUERY_KEY })
      const previous = qc.getQueryData<CacheStatsResponse>(CACHE_STATS_QUERY_KEY)
      if (previous) qc.setQueryData(CACHE_STATS_QUERY_KEY, clearCacheStatsEntries(previous))
      return { previous }
    },
    onSuccess: (stats) => {
      qc.setQueryData(CACHE_STATS_QUERY_KEY, clearCacheStatsEntries(stats))
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) qc.setQueryData(CACHE_STATS_QUERY_KEY, context.previous)
    },
  })
}
